// Considered: ArcStagesMolecule — rejected. It is bound to `ApproachArcConfig`
// (inline-definition/templateSystemName wrapper) and renders approach-specific chrome
// (source label, key dates, primary-arc badge) that has no home in a Studio browse
// drawer. This molecule reuses only the stage-row shape, driven straight off
// `ArcTemplateDefinition.stages`. See spec Design System Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { StageDefinition } from 'types/arc';
import TagAtom, { type TagTone } from '../../atoms/Tag/TagAtom';
import './ArcStageListMolecule.scss';

export interface ArcStageListMoleculeProps {
  stages: StageDefinition[];
  className?: string;
}

const TRANSITION_TONE: Record<string, TagTone> = {
  manual: 'outline',
  auto: 'info',
  calculated: 'accent',
};

const ArcStageListMolecule: React.FC<ArcStageListMoleculeProps> = ({
  stages,
  className = '',
}) => {
  const { t } = useTranslation();
  const rootClasses = ['arc-stage-list', className].filter(Boolean).join(' ');

  if (stages.length === 0) {
    return (
      <p className={`${rootClasses} arc-stage-list--empty`}>
        {t('studio.gallery.drawer.arc.noStages')}
      </p>
    );
  }

  return (
    <ul className={rootClasses} data-testid="arc-stage-list">
      {stages.map((stage, index) => {
        const transitionType = stage.transition?.type ?? 'manual';
        const onEnterCount = stage.onEnter?.length ?? 0;
        const onExitCount = stage.onExit?.length ?? 0;
        const intentCount = stage.intents?.length ?? 0;

        return (
          <li
            key={stage.id}
            className="arc-stage-list__row"
            data-testid={`arc-stage-row-${stage.id}`}
          >
            <span className="arc-stage-list__index" aria-hidden="true">
              {index + 1}
            </span>
            <span className="arc-stage-list__name">{stage.fallbackName || stage.id}</span>
            <TagAtom
              size="small"
              tone={TRANSITION_TONE[transitionType] ?? 'outline'}
              label={t(`studio.gallery.drawer.arc.transitionKind.${transitionType}`)}
            />
            <span className="arc-stage-list__meta-group">
              {onEnterCount > 0 && (
                <span className="arc-stage-list__meta">
                  {t('studio.gallery.drawer.arc.onEnterCount', { count: onEnterCount })}
                </span>
              )}
              {onExitCount > 0 && (
                <span className="arc-stage-list__meta">
                  {t('studio.gallery.drawer.arc.onExitCount', { count: onExitCount })}
                </span>
              )}
              {intentCount > 0 && (
                <span className="arc-stage-list__meta">
                  {t('studio.gallery.drawer.arc.intentCount', { count: intentCount })}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

ArcStageListMolecule.displayName = 'ArcStageListMolecule';

export default ArcStageListMolecule;
